import React, { useCallback, useEffect, useMemo } from 'react';
import { Select, Modal, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  SubmissionResponseDtoStatusEnum,
  UpdateSubmissionStatusPayloadDtoStatusEnum,
} from 'libs/pinnacle-sdk';
import { UpdateSubmissionStatusModalProps } from './types';
import { SortedSelect } from 'components/ui/sorted-select';

const UpdateSubmissionStatusModal: React.FC<
  UpdateSubmissionStatusModalProps
> = ({ onCancel, open, onSubmit, submissionStatus, isReadyToComplete }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const handleSubmit = useCallback(() => {
    form.validateFields().then((values) => {
      onSubmit?.(values);
    });
  }, [form, onSubmit]);

  const allowedStatuses = useMemo(() => {
    if (submissionStatus === SubmissionResponseDtoStatusEnum.Declined) {
      return [UpdateSubmissionStatusPayloadDtoStatusEnum.Pending];
    }
    return [
      ...(submissionStatus === SubmissionResponseDtoStatusEnum.Quoted ||
      submissionStatus === SubmissionResponseDtoStatusEnum.Prospect
        ? [UpdateSubmissionStatusPayloadDtoStatusEnum.Pending]
        : []),
      ...(isReadyToComplete
        ? [UpdateSubmissionStatusPayloadDtoStatusEnum.Complete]
        : []),
      UpdateSubmissionStatusPayloadDtoStatusEnum.Declined,
      UpdateSubmissionStatusPayloadDtoStatusEnum.Cancelled,
    ];
  }, [submissionStatus, isReadyToComplete]);
  useEffect(() => {
    form.setFieldsValue({
      status: allowedStatuses[0],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedStatuses]);

  return (
    <Modal
      title={
        <div className="text-2xl font-medium">
          {t('updateSubmissionStatusModal.title')}
        </div>
      }
      open={open}
      onCancel={onCancel}
      okText={t('updateSubmissionStatusModal.confirm')}
      cancelText={t('general.cancel')}
      onOk={handleSubmit}
      width={640}
      centered
      destroyOnClose
      okButtonProps={{
        color: 'primary',
        variant: 'solid',
        size: 'large',
      }}
      cancelButtonProps={{
        variant: 'outlined',
        color: 'danger',
        size: 'large',
      }}
    >
      <Form form={form} layout="vertical" className="py-3">
        <Form.Item
          label={t('updateSubmissionStatusModal.submissionStatus')}
          name="status"
          rules={[
            {
              required: true,
              message: t('updateSubmissionStatusModal.statusRequired'),
            },
          ]}
        >
          <SortedSelect
            size="large"
            options={allowedStatuses.map((status) => ({
              label: status,
              value: status,
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UpdateSubmissionStatusModal;
